import { Flex, Button, useDisclosure } from '@chakra-ui/react';
import { FileUpload } from '@texas/types';
import { Accept, FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { FileUploads } from './FileUploads';
import { DropZone } from './DropZone';
import { Icons } from '../Icons';
import { imageMimeTypes } from '@texas/utils/helpers/filesHelper';
import { CapturePhoto } from '../capture/CapturePhoto';
import { CapturePhotoProps } from './shared';

export function FileDropzone({
  accept,
  isDisabled = false,
  multiple = false,
  onUpload,
  isUploading,
  uploadFailed,
  fileUploads,
  removeFileUpload,
  onFileBrowserOpen,
  validator,
  capturePhotoProps,
  compact,
}: {
  compact?: { description: string; buttonLabel: string };
  accept?: Accept;
  isDisabled?: boolean;
  multiple?: boolean;
  onUpload: (files: File[]) => void;
  isUploading: boolean;
  uploadFailed: boolean;
  fileUploads: FileUpload[];
  removeFileUpload: (file: FileUpload) => void;
  onFileBrowserOpen?: () => void;
  validator?: <T extends File>(
    file: T,
  ) => FileError | readonly FileError[] | null;
  capturePhotoProps?: CapturePhotoProps;
}) {
  const { t } = useTranslation();
  const {
    isOpen: isCapturePhotoOpen,
    onClose: onCapturePhotoClose,
    onOpen: onCapturePhotoOpen,
  } = useDisclosure();

  return (
    <Flex direction="column" gap={2} flexGrow={1} alignItems="center">
      {(isUploading || uploadFailed) && (
        <FileUploads
          fileUploads={fileUploads}
          removeFileUpload={removeFileUpload}
        />
      )}
      {!isUploading && (
        <DropZone
          isDisabled={isDisabled}
          compact={compact}
          accept={accept}
          multiple={multiple}
          onUpload={onUpload}
          validator={validator}
        />
      )}
      {(onFileBrowserOpen || capturePhotoProps) && (
        <Flex>
          {onFileBrowserOpen && (
            <Button
              leftIcon={<Icons.FolderOutline />}
              variant="texas-link-light"
              textDecoration="underline"
              onClick={onFileBrowserOpen}
              isDisabled={isDisabled}
              isLoading={isUploading}
            >
              {t('fileBrowser.selectFromLibrary')}
            </Button>
          )}
          {capturePhotoProps && accept?.[imageMimeTypes.jpeg] !== undefined && (
            <Button
              variant="texas-link-light"
              textDecoration="underline"
              onClick={onCapturePhotoOpen}
              isDisabled={isDisabled}
              isLoading={isUploading}
              leftIcon={<Icons.Camera />}
            >
              {t('capturePhoto.capture')}
            </Button>
          )}
        </Flex>
      )}
      {capturePhotoProps && !isDisabled && (
        <CapturePhoto
          referenceId={capturePhotoProps.referenceId}
          referenceType={capturePhotoProps.referenceType}
          text1={capturePhotoProps.text1}
          text2={capturePhotoProps.text2}
          isOpen={isCapturePhotoOpen}
          onClose={onCapturePhotoClose}
          onPhotoReceived={(file) => {
            capturePhotoProps.onPhotoReceived(file);
            onCapturePhotoClose();
          }}
        />
      )}
    </Flex>
  );
}
